/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$step-small-radius: 16px !default;
$step-large-radius: 32px !default;
$step-default-shadow: none !default;
$step-connector-height: 2px !default;
$step-carrier-height: 112px !default;
$step-carrier-width: 112px !default;
$step-carrier-inactive-width: 96px !default;
$step-carrier-inactive-height: 96px !default;
$step-carrier-done-height: 48px !default;
$step-carrier-done-width: 48px !default;

$step-small-size: 24px !default;
$step-medium-size: 32px !default;
$step-large-size: 48px !default;

$step-active-circle-border-width: 8px !default;
$step-active-circle-small-border-width: 6px !default;
$step-active-circle-large-border-width: 12px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: block;
  width: 100%;
}

.#{$prefix}pictogram-step-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  list-style: none;

  &.#{$prefix}first-item {
    .#{$prefix}connector {
      display: none;
    }
  }

  .#{$prefix}wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    height: 100%;

    a:hover ~ .#{$prefix}description {
      text-decoration: underline;
    }
  }

  .#{$prefix}description {
    padding-top: 20px;
    padding-left: 16px;
    padding-right: 16px;
    color: v(inactiveTextColor);
    font-family: $font-family-base;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    text-align: center;

    &.#{$prefix}active {
      color: v(activeTextColor);
      font-weight: $font-weight-bold;
    }

    &.#{$prefix}done {
      color: v(doneTextColor);
    }

    .#{$prefix}mobile {
      font-size: 14px;
      line-height: 18px;
    }

    &.#{$prefix}visibility-none {
      visibility: hidden;
    }
  }

  .#{$prefix}container {
    position: relative;
    width: auto;
    height: $step-carrier-height;
    display: grid;
    grid-template-areas: "content";
    justify-items: center;
    align-items: center;
    fill: v(inactiveStepColor);
    z-index: 1;

    .#{$prefix}carrier {
      width: $step-carrier-width;
      height: $step-carrier-height;
      background-color: v(inactiveStepColor);
      box-shadow: $step-default-shadow;
      border-radius: $step-large-radius;
    }

    & > * {
      grid-area: content;
    }

    &.#{$prefix}active {
      fill: v(activeStepColor);

      .#{$prefix}carrier {
        background-color: v(activeStepColor);
        border-radius: $step-large-radius;
      }

      &.#{$prefix}error {
        .#{$prefix}carrier {
          background-color: v(errorStepColor);
          & ~ eon-ui-icon {
            fill: v(errorIconColor);
          }
        }

        & ~ .#{$prefix}description {
          color: v(errorTextColor);
        }
      }
    }

    &.#{$prefix}inactive {
      .#{$prefix}pictogram {
        fill: v(inactiveStepColor);
      }

      .#{$prefix}carrier {
        background-color: v(parentBackgroundColor);
        border: 2px solid v(inactiveStepColor);
        height: $step-carrier-inactive-height;
        width: $step-carrier-inactive-width;
      }
    }

    &.#{$prefix}done {
      fill: v(doneStepColor);

      .#{$prefix}carrier {
        height: $step-carrier-done-height;
        width: $step-carrier-done-width;
        background-color: v(doneStepColor);
        border-radius: $step-small-radius;
      }

      &.#{$prefix}error {
        .#{$prefix}carrier {
          background-color: v(errorStepColor);
          & ~ eon-ui-icon {
            fill: v(errorIconColor);
          }
        }

        & ~ .#{$prefix}description {
          color: v(errorTextColor);
        }
      }
    }

    .#{$prefix}pictogram {
      fill: v(parentBackgroundColor);

      &.#{$prefix}active {
        fill: v(activeIconColor);
      }
    }

    .#{$prefix}icon {
      fill: v(parentBackgroundColor);

      &.#{$prefix}done {
        fill: v(doneIconColor);
      }
    }

    &:hover,
    &:focus-within {
      ~ .#{$prefix}description {
        &.#{$prefix}visibility-none {
          visibility: visible;
        }
      }
    }
  }

  .#{$prefix}connector {
    position: absolute;
    top: 56px;
    transform: translateY(-50%);
    z-index: 0;
    width: 100%;
    left: -50%;
    border-bottom: 2px solid v(inactiveStepColor);

    &.#{$prefix}done,
    &.#{$prefix}active {
      background: v(activeStepColor);
      &.#{$prefix}error {
        border-bottom-color: v(errorStepColor);
      }
    }

    &.#{$prefix}inactive {
      border-bottom-style: dotted;
    }
  }

  .#{$prefix}mobile {
    height: 32px;
    width: 32px;
    border-radius: 50%;
    background: v(inactiveStepColor);
    background-clip: padding-box;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    &.#{$prefix}active {
      border: 8px solid v(activeStepShadowColor);
      background: v(activeIconColor);
      background-clip: padding-box;
      box-shadow: 0px 0px 0px 8px var(--activeStepShadowColor);
      box-sizing: border-box;

      &.#{$prefix}error {
        background: v(errorStepColor);
        border-color: v(errorStepColor);
        box-shadow: 0px 0px 0px 8px v(errorStepShadowColor);

        eon-ui-icon {
          fill: v(errorIconColor);
        }

        & ~ .#{$prefix}description {
          color: v(errorTextColor);
        }
      }
    }

    &.#{$prefix}done {
      background: v(activeStepColor);
      background-clip: padding-box;

      &.#{$prefix}error {
        background: v(errorStepColor);

        eon-ui-icon {
          fill: v(errorIconColor);
        }

        & ~ .#{$prefix}description {
          color: v(errorTextColor);
        }
      }
    }
  }

  &.#{$prefix}vertical {
    padding-left: 8px;
    width: auto;
    .#{$prefix}wrapper {
      justify-content: flex-start;
      align-items: flex-start;
      padding-left: 32px;
    }

    .#{$prefix}description {
      text-align: left;
      padding: 4px 16px 32px;
      min-height: 24px;
    }

    .#{$prefix}mobile {
      position: absolute;
      left: 0;
      top: 0;
    }

    .#{$prefix}connector {
      height: 100%;
      width: 2px;
      left: 16px;
      transform: translateX(-50%);
      top: -36px;

      &.#{$prefix}active,
      &.#{$prefix}inactive {
        box-sizing: border-box;
        border-left: 2px dotted v(inactiveStepColor);
        border-bottom-width: 0;
        background: none;
      }

      &.#{$prefix}active {
        &.#{$prefix}error {
          border-left-color: v(errorStepColor);
        }
      }

      &.#{$prefix}done {
        &.#{$prefix}error {
          background: v(errorStepColor);
        }
      }
    }

    .#{$prefix}wrapper {
      position: relative;
    }

    &.#{$prefix}first-item {
      .#{$prefix}connector {
        display: block;
      }
    }

    &.#{$prefix}last-item {
      .#{$prefix}connector {
        display: none;
      }
    }

    .#{$prefix}connector {
      top: 16px;
    }

    &.#{$prefix}inactive {
      .#{$prefix}mobile {
        background: var(--parentBackgroundColor);
        border: 2px solid var(--inactiveStepColor);
        box-sizing: border-box;
      }
    }
  }

  @include for-size(phone) {
    .#{$prefix}wrapper {
      justify-content: flex-start;
      align-items: flex-start;
      padding-left: 32px;
    }

    .#{$prefix}description {
      text-align: left;
      padding: 3px 16px 32px;
      min-height: 24px;
    }

    .#{$prefix}mobile {
      position: absolute;
      left: 0;
      top: 0;
    }

    .#{$prefix}connector {
      height: 100%;
      width: 2px;
      left: 16px;
      transform: translateX(-50%);
      top: -36px;
      box-sizing: border-box;
      border-bottom-width: 0;

      &.#{$prefix}inactive {
        border-left-style: dotted;
      }
    }
  }

  a {
    outline: none;

    &:focus-visible {
      .#{$prefix}container {
        .#{$prefix}carrier {
          @include focusBorder(v(focusColor), 2px, 3px solid);
          box-shadow: 0px 0px 0px 2px v(parentBackgroundColor);
        }
      }

      & ~ .#{$prefix}description {
        &.#{$prefix}visibility-none {
          visibility: visible;
        }
      }
    }

    &:hover {
      & ~ .#{$prefix}description {
        &.#{$prefix}visibility-none {
          visibility: visible;
        }
      }
    }
  }
}

.#{$prefix}circle-step-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  list-style: none;
  a {
    text-decoration: none;
    outline: none;

    &:hover ~ .#{$prefix}step-label {
      text-decoration: underline;

      &.#{$prefix}visibility-none {
        display: block;
      }
    }

    &:focus-visible {
      .#{$prefix}circle {
        @include focusBorder(v(focusColor), 2px, 3px solid);
        box-shadow: 0px 0px 0px 2px v(parentBackgroundColor);
      }

      & ~ .#{$prefix}step-label {
        &.#{$prefix}visibility-none {
          display: block;
        }
      }
    }
  }

  @include for-size(tablet) {
    min-width: calc(calc(100vw / 4) - 42px);
  }

  @include for-size(phone) {
    min-width: calc(calc(100vw / 2) - 32px);
  }

  .#{$prefix}connector {
    position: absolute;
    left: -50%;
    width: 100%;
    border-bottom: 2px solid v(inactiveStepColor);
    z-index: 0;
    display: block;
  }

  &.#{$prefix}first-item {
    .#{$prefix}connector {
      display: none;
    }
  }

  .#{$prefix}circle {
    position: relative;
    box-sizing: border-box;
    margin-bottom: 8px;
    background: v(parentBackgroundColor);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid v(inactiveStepColor);
    z-index: 1;
    flex-shrink: 0;

    .#{$prefix}step-icon {
      fill: v(inactiveIconColor);
    }

    .#{$prefix}step-number {
      font-family: $font-family-base;
      font-weight: 700;
      font-size: 16px;
      line-height: 100%;
      color: v(inactiveIconColor);
      text-decoration: none;
    }

    eon-ui-text {
      padding: 0;
    }

    eon-ui-tooltip {
      margin: 10px 0px !important;
      max-width: v(tooltipWidth);
    }

    // &:hover {
    //   ~ .#{$prefix}step-label {
    //     &.#{$prefix}visibility-none {
    //       display: block;
    //     }
    //   }
    // }
  }

  .#{$prefix}step-label {
    position: relative;
    font-family: $font-family-base;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    padding: 8px;
    color: v(inactiveTextColor);
    margin: 0;

    &.#{$prefix}visibility-none {
      display: none;
    }
  }

  &.#{$prefix}size-small {
    .#{$prefix}connector {
      top: calc($step-small-size * 0.5);
    }

    .#{$prefix}circle {
      height: $step-small-size;
      width: $step-small-size;

      .#{$prefix}step-number {
        font-size: 14px;
      }
    }

    .#{$prefix}step-label {
      font-size: 14px;
      line-height: 18px;
    }
  }

  &.#{$prefix}size-medium {
    .#{$prefix}connector {
      top: calc($step-medium-size * 0.5);
    }

    .#{$prefix}circle {
      height: $step-medium-size;
      width: $step-medium-size;

      .#{$prefix}step-number {
        font-size: 16px;
      }
    }
  }

  &.#{$prefix}size-large {
    .#{$prefix}connector {
      top: calc($step-large-size * 0.5);
    }

    .#{$prefix}circle {
      height: $step-large-size;
      width: $step-large-size;
      margin-bottom: 16px;

      .#{$prefix}step-number {
        font-size: 18px;
      }
    }
  }

  &.#{$prefix}active {
    .#{$prefix}connector {
      background: v(doneStepColor);
    }

    .#{$prefix}circle {
      background: v(activeIconColor);
      box-shadow: 0px 0px 0px 8px v(activeStepShadowColor);
      border-radius: 999px;
      border-color: v(activeStepColor);
      border-width: $step-active-circle-border-width;

      .#{$prefix}step-icon {
        fill: v(activeIconColor);
      }

      .#{$prefix}step-number {
        color: v(activeIconColor);
      }
    }

    .#{$prefix}step-label {
      color: v(activeTextColor);
      font-weight: bold;
    }

    &.#{$prefix}size-large {
      .#{$prefix}circle {
        border-width: $step-active-circle-large-border-width;
      }
    }

    &.#{$prefix}size-small {
      .#{$prefix}circle {
        border-width: $step-active-circle-small-border-width;
      }
    }

    &.#{$prefix}error {
      .#{$prefix}connector {
        background: v(errorStepColor);
        border-bottom: 2px solid var(--errorStepColor);
      }

      .#{$prefix}circle {
        background: v(errorStepColor) !important;
        box-shadow: 0px 0px 0px 8px v(errorStepShadowColor);
        border-color: v(errorStepColor);
      }

      &.#{$prefix}size-large {
        .#{$prefix}circle {
          box-shadow: 0px 0px 0px 12px v(errorStepShadowColor);
        }
      }
      .#{$prefix}step-label {
        color: v(errorTextColor);
      }

      .#{$prefix}step-icon {
        fill: v(errorIconColor);
      }

      .#{$prefix}step-number {
        color: v(errorIconColor);
      }

      eon-ui-tooltip {
        --backgroundColor: var(--errorStepColor);
        --innerContentColor: var(--errorIconColor);
      }
    }

    a:focus-visible {
      .#{$prefix}circle {
        @include focusBorder(v(focusColor), 10px, 3px solid);
        box-shadow: 0px 0px 0px 8px var(--activeStepShadowColor), 0px 0px 0px 10px var(--parentBackgroundColor);
      }
    }
  }

  &.#{$prefix}done,
  &.#{$prefix}incomplete {
    .#{$prefix}connector {
      background: v(doneStepColor);
    }

    .#{$prefix}circle {
      background: v(doneStepColor);
      border-color: v(doneStepColor);

      .#{$prefix}step-icon {
        fill: v(doneIconColor);
      }

      .#{$prefix}step-number {
        color: v(doneIconColor);
      }
    }

    .#{$prefix}step-label {
      color: v(doneTextColor);
    }

    &.#{$prefix}error {
      .#{$prefix}connector {
        background: v(errorStepColor);
        border-bottom: 2px solid var(--errorStepColor);
      }

      .#{$prefix}circle {
        background: v(errorStepColor) !important;
        border-color: v(errorStepColor);
      }

      .#{$prefix}step-label {
        color: v(errorTextColor);
      }

      .#{$prefix}step-icon {
        fill: v(errorIconColor);
      }

      .#{$prefix}step-number {
        color: v(errorIconColor);
      }

      eon-ui-tooltip {
        --backgroundColor: var(--errorStepColor);
        --innerContentColor: var(--errorIconColor);
      }
    }
  }

  &.#{$prefix}inactive {
    .#{$prefix}connector {
      border-bottom-style: dotted;
    }
  }

  &.#{$prefix}vertical {
    flex-direction: row;
    align-items: unset;
    justify-content: unset;
    text-align: left;
    min-height: 80px;

    .#{$prefix}connector {
      height: 100%;
      width: 2px;
      transform: translate(-50%, -100%);
    }

    .#{$prefix}circle {
      eon-ui-tooltip {
        margin: 0px !important;
      }
    }

    .#{$prefix}step-label {
      padding: 3px 16px 16px;
      width: 80%;
      min-height: 50px;
    }

    &.#{$prefix}size-small {
      padding-left: 8px;

      .#{$prefix}connector {
        left: calc(8px + $step-small-size * 0.5);
      }

      .#{$prefix}step-label {
        padding: 3px 16px 16px;
      }
    }

    &.#{$prefix}size-medium {
      padding-left: 8px;

      .#{$prefix}connector {
        left: calc(8px + $step-medium-size * 0.5);
      }
    }

    &.#{$prefix}size-large {
      padding-left: 12px;

      .#{$prefix}connector {
        left: calc(12px + $step-large-size * 0.5);
      }

      .#{$prefix}step-label {
        padding: 13px 20px 16px;
      }
    }

    &.#{$prefix}active,
    &.#{$prefix}inactive {
      .#{$prefix}connector {
        box-sizing: border-box;
        border-left: 2px dotted v(inactiveStepColor);
        border-bottom-width: 0;
        background: none;
      }
    }

    &.#{$prefix}first-item {
      .#{$prefix}connector {
        display: block;
      }
    }

    &.#{$prefix}last-item {
      .#{$prefix}connector {
        display: none;
      }
    }

    .#{$prefix}connector {
      transform: translate(-50%);
    }
  }

  &.#{$prefix}numbered {
    &.#{$prefix}active {
      .#{$prefix}circle {
        background: v(activeStepColor);
      }
    }

    &.#{$prefix}inactive {
      .#{$prefix}circle {
        .#{$prefix}step-number {
          color: v(inactiveStepColor);
        }
      }
    }
  }

  &.#{$prefix}icon {
    &.#{$prefix}active {
      .#{$prefix}circle {
        background-color: v(activeStepColor);
      }
    }
  }

  &.#{$prefix}tooltip-on-step {
    .#{$prefix}step-label {
      display: none !important;
    }
  }
}
